import { useSelector } from "react-redux";
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { RootState } from "../../../redux/store";
import { menuRoutes } from "../../../router/menu";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { IssueService } from "../../../services/issue/issueService";
import { useCallback, useEffect, useState } from "react";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import PageHeader from "../../../layout/shared/page-header";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { Button, Loader, Tooltip } from "rizzui";
import { setSelectedView } from "../../../redux/issuesProfileSlice";
import IssuesFormModal from "../components/IssuesFormModal";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import useIssuesStatus from "../../../hooks/api-calls/useIssuesStatus";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.issues.profile.info,
        permission: {
            group: 'issues',
            action: 'get',
        }
    },
    {
        label: 'Fotografias',
        path: menuRoutes.issues.profile.photos,
        permission: {
            group: 'issues_photos',
            action: 'get',
        }
    }

];


export default function IssuesProfileLayout({ children, isLoading, setRefetchTab }: { children?: React.ReactNode, isLoading?: boolean, setRefetchTab?: (state:boolean) => void }) {

    // STATES

    const [ issues, setIssues ] = useState<any>(null);
    const [isOpen, setIsOpen] = useState(false);


    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.issuesProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const service = new IssueService();
    const { getIssuesStatusList } = useIssuesStatus();


    // DATA FETCHING
    
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getIssueById(id);
        return response.getResponseData();
    }, [id]));

    const pageHeader = {
        title: `Incidencia ${data?.issueCode || 'Incidencia'}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Incidencias' },
            { name: 'Datos de la incidencia' },
        ],
    }


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR INCIDENCIA
     * @EN DELETE ISSUE
     * @param id
     * 
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteIssue(id)).getResponseData();
            toast.success(response.message);
            navigate(menuRoutes.issues.path);
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR EL MODAL
     * @EN CLOSE THE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose = () => {
        setIsOpen(false);

        setTimeout(() => {
            setIssues(null);
            setRefetchTab && setRefetchTab(true);
        }, 150);
    }
    //-----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO LA INCIDENCIA
     * @EN CHANGE THE STATUS OF THE ISSUE
     * 
     * @param id
     * @param status
     */ 
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusIssue(id, status)).getResponseData();
        if(response.success) {
            toast.success("Estado de la incidencia cambiada correctamente");
            navigate(menuRoutes.issues.path);
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.issues.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);



     return (
            <>
                <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                    <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                        { userCan('edit', 'issues')&& data && (
                            <>
                                <div className='flex flex-col gap-2 justify-content-center text-center mt-1' key={id + data.status?.id}>
                                        {(id && data.status?.id) ? (
                                            <StatusDropdown
                                                entityId={id}
                                                title="¿Estas seguro de cambiar el estado del Incidencias?"
                                                statesOptions={getIssuesStatusList()}
                                                currentState={data.status}
                                                handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(id, statusId)}
                                                hasComments={false}
                                            />
                                        ) : "N/A"}
                                </div>
                                <Tooltip content={'Editar incidencia'} placement={'top'} color={'invert'} size={'sm'}>
                                    <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                        setIsOpen(true);
                                        setIssues(data.id);
                                    }}>
                                        Editar
                                    </Button>
                                </Tooltip>
                             </>
                        )}
                        {userCan('delete', 'carrion') && (
                            <DeletePopover
                                title={`Eliminar Incidencia`}
                                description={`¿Estás seguro de que deseas eliminar el usuario?`}
                                size={'md'}
                                onDelete={() => handleDelete(id)}
                                actionIconClassName='h-10 w-10'
                            />
                        )}

                    </div>
                </PageHeader>
    
                <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />
    
                {isLoading ? <Loader height='60vh' /> : <Outlet />}

                <IssuesFormModal issues={issues} isOpen={isOpen} onClose={onClose} refetch={refetch} />
            </>
        );
}